import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, useMediaQuery } from '@mui/material';
import { HumbeeTabs, CustomButton } from '../../../v2/MUI_custom_component';
import IconReusable from '../../../components/IconReusable';
import DeliveryLocationCard from '../../../v2/Address/Screens/DeliveryLocationCard';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import AddAddressGPS from './AddAddressGPS';
import { addressDetailAction, deleteAddressAction } from './APIs/action';

const Address = () => {
  const dispatch = useDispatch();
  const companies = useSelector(
    (state) => state.AddressReducer?.Companies || [],
  );
  const currentTab = useSelector((state) =>
    Number(state.AddressReducer?.currentTab ?? 0),
  );
  const selectedCompany = useSelector(
    (state) => state.AddressReducer?.selectedCompany || null,
  );
  const [isGPSFlowOpen, setIsGPSFlowOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width: 1200px)');
  const [addressDetail, setAddressDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (addressDetail && addressDetail.firms) {
      const firms = addressDetail.firms.map((firm) => ({
        company_id: firm.company_id,
        company_name: firm.company_name,
      }));
      dispatch({ type: 'SET_COMPANIES', data: firms });
      if (firms.length > 0 && !selectedCompany) {
        const initialCompany = firms[0];
        dispatch({ type: 'SELCTED_COMPANY', data: initialCompany });
      }
    }
  }, [dispatch, addressDetail, selectedCompany]);

  const getAddress = async () => {
    setLoading(true);
    const { error, message, data } = await addressDetailAction();
    setLoading(false);
    if (!error) {
      setAddressDetail(data);
      const usedSapCodes = [];
      if (data?.firms) {
        data.firms.forEach((firm) => {
          if (firm.addresses) {
            firm.addresses.forEach((address) => {
              if (address.external_system_code) {
                usedSapCodes.push(address.external_system_code);
              }
            });
          }
        });
      }
      dispatch({ type: 'SET_USED_SAP_CODES', data: usedSapCodes });
    } else {
      toast.error(message);
    }
  };
  const { selectedEntity: selectedCompanyEntity } = useSelector(
    (state) => state.entityReducer,
  );
  const deleteAddress = async (addressId, companyId) => {
    setLoading(true);
    const payload = {
      address_id: addressId,
      user_esi_id: selectedCompanyEntity,
    };
    const { error, message, data } = await deleteAddressAction(payload);
    setLoading(false);
    if (!error) {
      toast.success(message);
      return true; // Return success status
    } else {
      toast.error(message);
      return false; // Return failure status
    }
  };
  useEffect(() => {
    getAddress();
  }, []);
  const handleTabChange = (event, newValue) => {
    const tabIndex = Number(newValue);
    const tabData = companies.length > 0 ? companies : addressDetail?.firms;
    dispatch({ type: 'SET_CURRENT_TAB', data: tabIndex });

    if (tabData && tabData[tabIndex]) {
      const selectedCompany = {
        company_id: tabData[tabIndex].company_id,
        company_name: tabData[tabIndex].company_name,
      };
      dispatch({ type: 'SELCTED_COMPANY', data: selectedCompany });
    }
  };
  const AddressCard = ({ address, onEdit, onDelete }) => (
    <Box sx={{ borderRadius: 2, background: '#fff' }}>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='flex-start'
        >
          <Typography variant='h3'>{address.address_tag_label}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {address.external_system_code && (
              <Box
                display='flex'
                sx={{ gap: 1 }}
              >
                <Typography
                  variant='labelM'
                  sx={{ color: '#666666' }}
                >
                  SAP Code{' '}
                </Typography>
                <Typography variant='p3'>
                  {address.external_system_code}{' '}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography variant='p1'>
                {address?.address_line_1}{' '}
                {address?.address_line_2 && <>, {address?.address_line_2}</>}
                <br />
                {address?.district_name && <> {address?.district_name}</>}
                {address?.location_name && <>, {address.location_name}</>}
                <br />
                {address?.state_name && <>{address?.state_name}</>}
                {address?.pincode_id && <>, {address?.pincode_id}</>}
              </Typography>
            </Box>
          </Box>
          {address.address_tag_key !== 'REGISTERED' && (
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
            >
              <Box
                onClick={() => onEdit(address)}
                sx={{
                  width: 24,
                  height: 24,
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <IconReusable
                  name='Edit'
                  size={isMobile ? 16 : 24}
                  color='#995A00'
                />
              </Box>
              <Box
                onClick={() => onDelete(address)}
                sx={{
                  width: 24,
                  height: 24,
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <IconReusable
                  name='Delete'
                  size={isMobile ? 16 : 24}
                  color='#995A00'
                />
              </Box>
            </Box>
          )}
        </Box>
        {address.whatsapp_no && (
          <Box
            display='flex'
            sx={{ gap: 1 }}
          >
            <Typography
              variant='labelM'
              sx={{ color: '#666666' }}
            >
              Whatsapp Number{' '}
            </Typography>
            <Typography variant='p3'>{address?.whatsapp_no} </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
  const handleEdit = (address) => {
    setEditingAddress(address);
    setIsEditMode(true);
    setIsGPSFlowOpen(true);
  };
  const handleDelete = async (address) => {
    const currentCompanyId = currentFirm?.company_id;
    if (currentCompanyId && address?.address_id) {
      const deleteSuccess = await deleteAddress(
        address.address_id,
        currentCompanyId,
      );
      if (deleteSuccess) {
        getAddress();
      }
    } else {
      toast.error('Unable to delete address. Missing required information.');
    }
  };
  const handleAddNewAddress = () => {
    setEditingAddress(null);
    setIsEditMode(false);
    setIsGPSFlowOpen(true);
  };
  const handleGPSFlowClose = () => {
    setIsGPSFlowOpen(false);
    setEditingAddress(null);
    setIsEditMode(false);
  };
  const handleGPSFlowSuccess = (addressData) => {
    setIsGPSFlowOpen(false);
    setEditingAddress(null);
    setIsEditMode(false);
    getAddress();
  };
  const tabData = companies.length > 0 ? companies : addressDetail?.firms;
  const currentFirm = selectedCompany?.company_id
    ? addressDetail?.firms?.find(
        (firm) => firm.company_id === selectedCompany.company_id,
      )
    : addressDetail?.firms?.[currentTab];

  const { is_PWA } = useSelector((state) => {
    return {
      is_PWA: state.loginReducer.is_PWA,
    };
  });
  return (
    <>
      <Box sx={{ p: { xs: 2, md: 2 }, pt: { xs: 2, md: 0 } }}>
        <Box mb={0.5}>
          <Typography variant='h2'>Address Details</Typography>
        </Box>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          mb={!isMobile && 0.5}
        >
          <HumbeeTabs
            tabVariant={isMobile ? 'small' : 'default'}
            data={tabData}
            currentTab={currentTab}
            onChange={handleTabChange}
            getTabLabel={(firm) => firm.company_name}
            getTabKey={(firm) => firm.company_id}
            tabType='dashed'
            variant='scrollable'
            scrollButtons='auto'
            scrollVisible={isMobile && companies.length > 3}
          />
          {!is_PWA && !isMobile && (
            <CustomButton
              variant='outlined'
              label='Add New Address'
              onClick={handleAddNewAddress}
            />
          )}
        </Box>
        {!is_PWA && isMobile && (
          <Box mb={2}>
            <CustomButton
              fullWidth
              variant='outlined'
              label='Add New Address'
              onClick={handleAddNewAddress}
            />
          </Box>
        )}
        <Grid
          container
          spacing={{ xs: 2, md: 2 }}
        >
          {currentFirm?.addresses?.map((address) => (
            <Grid
              item
              xs={12}
              md={6}
              key={address.address_id}
            >
              <AddressCard
                key={address.address_id}
                address={address}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            </Grid>
          ))}
          <Grid
            item
            xs={12}
            md={6}
          >
            {currentFirm?.show_add_reward_delivery_address_card && (
              <DeliveryLocationCard
                non_reward={false}
                onAddressAdded={getAddress}
              />
            )}
          </Grid>
        </Grid>
      </Box>
      <AddAddressGPS
        isOpen={isGPSFlowOpen}
        onClose={handleGPSFlowClose}
        onSuccess={handleGPSFlowSuccess}
        editingAddress={editingAddress}
        isEditMode={isEditMode}
        currentFirm={currentFirm}
      />
    </>
  );
};

export default Address;
